import { Stack } from "@chakra-ui/react";
import { FiSettings } from "react-icons/fi";
import { DropdownField } from "components/dropdown-field";
import { FilterCard, FilterGrid } from "../components/FilterCard";
import { SidePanelAccordion } from "../components/SidePanelAccordion";
import { State } from "components/pipeline-components/classes/state-machine/state";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { MailingTableRow } from "components/mailing-components/mailing-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "api/api";
import apiDigitacao from "api/api-digitacao";
import { Toast } from "components/toast";
import { UsuarioBanco } from "components/usuarios-bancos-components/page";
import { DiagnosticPanel } from "./DiagnosticPanel";

interface TabelaDigitacaoProps {
  banco: string;
  codigo: string;
  convenio: string;
  descricao: string;
  prazo: null;
}

type DigitacaoStateField =
  | "tipoColunaMargem"
  | "banco"
  | "usuarioDigitacao"
  | "usuarioDigitacaoSenha"
  | "tabela"
  | "cpfUsuarioCertificado"
  | "convenio";

export function DigitacaoEditor({
  isDisabled,
  digitacaoState,
  updatePipelineData,
  mailingData,
  selectedStateId,
  pipelineData,
  diagnosticUpdatePipelineData
}: {
  isDisabled: boolean;
  digitacaoState?: State;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  mailingData: Partial<MailingTableRow> | undefined;
  selectedStateId?: number;
  pipelineData?: StatesMachine;
  diagnosticUpdatePipelineData?: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}) {
  const [usuariosBancos, setUsuariosBancos] = useState<UsuarioBanco[]>([]);
  const [tabelas, setTabelas] = useState<TabelaDigitacaoProps[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingTabelas, setLoadingTabelas] = useState(false);

  const banco = digitacaoState?.banco;
  const convenio = mailingData?.convenio;

  const usuarioDataDigitacao = useMemo(() => {
    return new Map(usuariosBancos.map((user) => [user.username, user]));
  }, [usuariosBancos]);

  const usuariosDigitacaoOptions = useMemo(
    () =>
      usuariosBancos.map((user) => ({
        name: user.username,
        value: user.username,
        completedUser: user
      })),
    [usuariosBancos]
  );

  const tabelasOptions = useMemo(
    () =>
      tabelas.map((tabela) => ({
        name: `${tabela.codigo} - ${tabela.descricao}`,
        value: tabela.codigo
      })),
    [tabelas]
  );

  const updateDigitacaoField = useCallback(
    (field: DigitacaoStateField, value: string) => {
      updatePipelineData((draft) => {
        draft.updateOperationState("DIGITACAO", (state) => {
          state[field] = value;
        });
        return;
      });
    },
    [updatePipelineData]
  );

  const getUsuariosBancos = useCallback(
    async (selectedBanco: string) => {
      setLoadingUsuarios(true);
      try {
        const { data } = await api.get<UsuarioBanco[]>(
          `usuarios-bancos/digitadores/${selectedBanco}`,
          {
            params: {
              customer_id: mailingData?.customerId
            }
          }
        );
        if (!data?.length) {
          Toast({
            title: "Não há usuários cadastrados para o banco selecionado",
            duration: 5000
          });
        }
        setUsuariosBancos(data);
      } catch (error) {
        Toast({
          title: "Erro ao buscar lista de Usuario Banco",
          status: "error"
        });
      } finally {
        setLoadingUsuarios(false);
      }
    },
    [mailingData?.customerId]
  );

  const getTabelasDigitacao = useCallback(
    async (selectedBanco: string) => {
      if (!convenio) {
        setTabelas([]);
        return;
      }

      setLoadingTabelas(true);
      try {
        const { data } = await apiDigitacao.get<TabelaDigitacaoProps[]>(
          `api/tabelas-digitacao/enable`,
          {
            params: {
              convenio,
              banco: selectedBanco,
              customer_id: mailingData?.customerId
            }
          }
        );
        if (!data?.length) {
          Toast({ title: "Não há tabelas para o banco selecionado" });
        }
        setTabelas(data);
      } catch (e) {
        Toast({ title: "Erro ao obter tabelas" });
      } finally {
        setLoadingTabelas(false);
      }
    },
    [convenio, mailingData?.customerId]
  );

  useEffect(() => {
    if (!convenio) return;
    updateDigitacaoField("convenio", convenio);
  }, [convenio, updateDigitacaoField]);

  useEffect(() => {
    if (!banco) {
      setUsuariosBancos([]);
      setTabelas([]);
      return;
    }

    getUsuariosBancos(banco);
    getTabelasDigitacao(banco);
  }, [banco, getTabelasDigitacao, getUsuariosBancos]);

  const tipoOperacao = mailingData?.tipoOperacao;
  const bancoOptions = ["REFIN_CARTEIRA"].includes(tipoOperacao!)
    ? bancosDigitacaoMailingRefinCarteira
    : bancosDigitacaoMailingPipeline;

  return (
    <Stack spacing="12px">
      <SidePanelAccordion
        icon={FiSettings}
        iconBgColor="#EDE9FE"
        iconColor="#7C3AED"
        title="Configuração"
        subtitle="Banco, tabela e usuário de digitação"
        activeCount={0}
      >
        <FilterGrid>
          <FilterCard label="Banco" isDisabled={isDisabled} fullWidth>
            <DropdownField
              isDisabled={isDisabled}
              options={bancoOptions}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
              value={digitacaoState?.banco}
              onChange={(value) => {
                updateDigitacaoField("banco", value);
                updateDigitacaoField("usuarioDigitacao", "");
                updateDigitacaoField("usuarioDigitacaoSenha", "");
                updateDigitacaoField("cpfUsuarioCertificado", "");
                updateDigitacaoField("tabela", "");
              }}
            />
          </FilterCard>

          <FilterCard label="Tabela" isDisabled={isDisabled} fullWidth>
            <DropdownField
              isDisabled={isDisabled}
              options={tabelasOptions}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
              value={digitacaoState?.tabela}
              onChange={(value) => updateDigitacaoField("tabela", value)}
              isLoading={loadingTabelas}
            />
          </FilterCard>

          <FilterCard
            label="Usuário digitação"
            isDisabled={isDisabled}
            fullWidth
          >
            <DropdownField
              isDisabled={isDisabled}
              options={usuariosDigitacaoOptions}
              dropdownProps={{ w: "100%", variant: "filterInput" }}
              value={digitacaoState?.usuarioDigitacao}
              onChange={(username, option) => {
                const selectedUser =
                  option?.completedUser ?? usuarioDataDigitacao.get(username);

                updateDigitacaoField("usuarioDigitacao", username);
                updateDigitacaoField(
                  "usuarioDigitacaoSenha",
                  selectedUser?.password || ""
                );
                updateDigitacaoField(
                  "cpfUsuarioCertificado",
                  selectedUser?.cpfUsuarioCertificado || ""
                );
              }}
              isLoading={loadingUsuarios}
            />
          </FilterCard>

          {tipoOperacao !== "REFIN_CARTEIRA" && (
            <FilterCard
              label="Tipo coluna margem"
              isDisabled={isDisabled}
              fullWidth
            >
              <DropdownField
                isDisabled={isDisabled}
                options={[
                  { name: "Margem Livre", value: "MARGEM_LIVRE" },
                  { name: "Margem Aumento", value: "MARGEM_AUMENTO" }
                ]}
                dropdownProps={{ w: "100%", variant: "filterInput" }}
                value={digitacaoState?.tipoColunaMargem}
                onChange={(value) =>
                  updateDigitacaoField("tipoColunaMargem", value)
                }
              />
            </FilterCard>
          )}
        </FilterGrid>
      </SidePanelAccordion>
      {selectedStateId != null &&
        pipelineData &&
        diagnosticUpdatePipelineData && (
          <DiagnosticPanel
            selectedStateId={selectedStateId}
            pipelineData={pipelineData}
            operation="DIGITACAO"
            updatePipelineData={diagnosticUpdatePipelineData}
          />
        )}
    </Stack>
  );
}

const bancosDigitacaoMailingRefinCarteira = [
  { name: "C6", value: "C6" },
  { name: "Daycoval", value: "DAYCOVAL" }
];

export const bancosDigitacaoMailingPipeline = [
  { name: "Bmg", value: "BMG" },
  { name: "Facta", value: "FACTA" },
  { name: "C6", value: "C6" },
  { name: "iCred", value: "ICRED" },
  { name: "Presença", value: "PRESENCA" },
  { name: "Pan", value: "PAN" }
];
